/**
 * MatchManagerModal.js - Gestione sequenza assalti stile FIE (Generico NvN)
 * 
 * Funzionalità:
 * 1. Supporta dynamicamente 2v2, 3v3, 4v4
 * 2. Mostra gli ultimi N assalti (dove N = team size)
 * 3. Deduce i ruoli (Pos 1..N) dalla selezione e rigenera la sequenza
 * 4. Gestione riserve (aggiornata)
 */

import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, Modal, TouchableOpacity, ScrollView, TextInput, StyleSheet } from 'react-native';
import { getActualFencer } from '../../utils/substitutionLogic';

import AssignmentIcon from '@mui/icons-material/Assignment';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FencingSwordsIcon, FencingMaskIcon } from '../FencingIcons';

// Sequenze FIE
const FIE_SEQUENCES = {
    // 2v2 (4 assalti)
    4: [[0, 0], [1, 1], [0, 1], [1, 0]],

    // 3v2 (6 assalti) - A=2, B=3 (ma la funzione getSequence3vs2 dice 2 rossi, 3 verdi? No, l'header dice ROSSI: 2, VERDI: 3. Ma sequence è 6.
    // getSequence3vs2 in fencingLogic: [[0,0], [1,0], [1,1], [0,2], [1,2], [0,1]] -> Qui A ha indici 0,1 (2 pers), B ha 0,1,2 (3 pers).
    // Quindi 3vs2 nel nome file significa "3 team vs 2 team"? O viceversa?
    // Di solito "3vs2" si intende nRed vs nGreen.
    // Se getSequence3vs2 ha A[0..1] e B[0..2], allora è un match 2(Red) vs 3(Green).
    // La chiameremo '2v3'.
    // Viceversa getSequence2vs3 -> A[0..2] e B[0..1] -> 3(Red) vs 2(Green). '3v2'.

    // Caso A(3) vs B(2) -> 3v2
    '3v2': [[0, 0], [1, 1], [2, 0], [0, 1], [1, 0], [2, 1]],

    // Caso A(2) vs B(3) -> 2v3
    '2v3': [[0, 0], [1, 1], [0, 2], [1, 0], [0, 1], [1, 2]],

    // 3v3 (9 assalti)
    9: [[2, 2], [0, 1], [1, 0], [0, 2], [2, 0], [1, 1], [0, 0], [1, 2], [2, 1]],

    // 4v4 (16 assalti)
    16: [
        [3, 3], [0, 0], [1, 1], [2, 2],
        [0, 3], [3, 0], [2, 1], [1, 2],
        [0, 1], [1, 0], [2, 3], [3, 2],
        [2, 0], [1, 3], [0, 2], [3, 1]
    ]
};

const MatchManagerModal = ({
    visible,
    onClose,
    assalti,
    teamA,
    teamB,
    currentAssalto,
    onUpdateAssalti,
    onAddSostituzione
}) => {
    const [activeTab, setActiveTab] = useState('sequenza');

    // Calcoli match params
    const totalAssalti = assalti.length;
    const nA = teamA?.members?.length || 0;
    const nB = teamB?.members?.length || 0;

    // Identifica il formato
    let matchFormatKey = null;
    let teamSize = 0; // Max size tra i due team

    if (nA === nB) {
        if (nA === 2 && totalAssalti === 4) { matchFormatKey = 4; teamSize = 2; }
        else if (nA === 3 && totalAssalti === 9) { matchFormatKey = 9; teamSize = 3; }
        else if (nA === 4 && totalAssalti === 16) { matchFormatKey = 16; teamSize = 4; }
    } else {
        // Casi asimmetrici (6 assalti)
        if (totalAssalti === 6) {
            if (nA === 3 && nB === 2) { matchFormatKey = '3v2'; teamSize = 3; } // A3 vs B2
            else if (nA === 2 && nB === 3) { matchFormatKey = '2v3'; teamSize = 3; } // A2 vs B3
        }
    }

    const isStandardFormat = matchFormatKey !== null;

    // Numero assalti finali da mostrare: 3 (default) o teamSize (se simmetrico > 3).
    // Per 3v2/2v3 (6 assalti), mostriamo gli ultimi 3.
    const numFinalBouts = (matchFormatKey === 16) ? 4 : (matchFormatKey === 4 ? 2 : 3);

    const startOfFinal = Math.max(0, totalAssalti - numFinalBouts);

    // Stato selezione
    const [finalSelection, setFinalSelection] = useState([]);
    const [reserveName, setReserveName] = useState('');
    const [selectedFencerToReplace, setSelectedFencerToReplace] = useState(null);
    const [selectedTeamSide, setSelectedTeamSide] = useState(null);

    const matchStarted = useMemo(() => assalti.some(a => a.completed), [assalti]);

    const teamAMembers = useMemo(() => {
        if (!teamA?.members) return [];
        return teamA.members.map((m, i) => ({
            id: m.id ?? i,
            name: typeof m === 'string' ? m : m.name,
            index: i
        }));
    }, [teamA]);

    const teamBMembers = useMemo(() => {
        if (!teamB?.members) return [];
        return teamB.members.map((m, i) => ({
            id: m.id ?? i,
            name: typeof m === 'string' ? m : m.name,
            index: i
        }));
    }, [teamB]);

    // Init
    useEffect(() => {
        if (totalAssalti >= numFinalBouts) {
            const newSelection = [];
            for (let i = 0; i < numFinalBouts; i++) {
                const assaltoIdx = startOfFinal + i;
                const assalto = assalti[assaltoIdx];
                if (assalto) {
                    const fObjA = assalto.actualFencerA || assalto.fencerA;
                    const fObjB = assalto.actualFencerB || assalto.fencerB;
                    const nameA = typeof fObjA === 'string' ? fObjA : fObjA?.name;
                    const nameB = typeof fObjB === 'string' ? fObjB : fObjB?.name;

                    newSelection.push([
                        teamAMembers.findIndex(m => m.name === nameA),
                        teamBMembers.findIndex(m => m.name === nameB)
                    ]);
                } else {
                    newSelection.push([null, null]);
                }
            }
            setFinalSelection(newSelection);
        }
    }, [visible, totalAssalti, assalti, teamAMembers, teamBMembers, startOfFinal, numFinalBouts]);

    const generateFullSequence = () => {
        if (finalSelection.some(sel => sel[0] === null || sel[0] === -1 || sel[1] === null || sel[1] === -1)) {
            alert('Seleziona tutti i tiratori per gli ultimi assalti');
            return;
        }

        if (isStandardFormat) {
            const sequenceIndices = FIE_SEQUENCES[matchFormatKey];

            // Mappe Ruolo -> Indice Membro
            const roleMapA = {};
            const roleMapB = {};

            // Logica simulazione ultimi N assalti per dedurre ruoli
            // finalSelection[i] corrisponde all'assalto (startOfFinal + i) della sequenza ufficiale

            for (let i = 0; i < numFinalBouts; i++) {
                const fieIndex = startOfFinal + i;
                const [rolePosA, rolePosB] = sequenceIndices[fieIndex]; // Indici FIE previsti (es. A1, B2)

                const selectedMembA = finalSelection[i][0];
                const selectedMembB = finalSelection[i][1];

                // Check congruenza A
                if (roleMapA[rolePosA] !== undefined && roleMapA[rolePosA] !== selectedMembA) {
                    alert(`Incongruenza Squadra A: il ruolo ${rolePosA + 1} è stato assegnato a due persone diverse.`);
                    return;
                }
                roleMapA[rolePosA] = selectedMembA;

                // Check congruenza B
                if (roleMapB[rolePosB] !== undefined && roleMapB[rolePosB] !== selectedMembB) {
                    alert(`Incongruenza Squadra B: il ruolo ${rolePosB + 1} è stato assegnato a due persone diverse.`);
                    return;
                }
                roleMapB[rolePosB] = selectedMembB;
            }

            // Verifica che tutti i ruoli necessari siano coperti
            // I ruoli necessari sono 0..nA-1 per A, 0..nB-1 per B
            const neededRolesA = Array.from({ length: nA }, (_, i) => i);
            const neededRolesB = Array.from({ length: nB }, (_, i) => i);

            const missingA = neededRolesA.filter(r => roleMapA[r] === undefined);
            const missingB = neededRolesB.filter(r => roleMapB[r] === undefined);

            if (missingA.length > 0 || missingB.length > 0) {
                alert(`Errore: La selezione degli ultimi assalti non permette di dedurre tutti i ruoli. Assicurati che ogni tiratore di Squadra A (${nA} tiratori) e Squadra B (${nB} tiratori) compaia almeno una volta negli ultimi assalti chiave.`);
                return;
            }

            // Rigenera
            const newAssalti = sequenceIndices.map((pair, idx) => {
                const originalAssalto = assalti[idx] || {};
                const memberIdxA = roleMapA[pair[0]];
                const memberIdxB = roleMapB[pair[1]];

                return {
                    ...originalAssalto,
                    id: idx,
                    fencerA: teamAMembers[memberIdxA],
                    fencerB: teamBMembers[memberIdxB],
                    actualFencerA: null,
                    actualFencerB: null
                };
            });

            onUpdateAssalti(newAssalti);
            onClose();

        } else {
            alert("Formato non supportato per la rigenerazione FIE completa.");
            onClose();
        }
    };

    const handleAddReserve = () => {
        if (!reserveName.trim() || !selectedFencerToReplace || !selectedTeamSide) return;

        const reserve = { name: reserveName.trim(), id: `reserve_${Date.now()}` };

        const newAssalti = assalti.map((assalto, index) => {
            // Se l'assalto è completato, non cambiarlo
            if (assalto.completed) return assalto;
            if (index < currentAssalto) return assalto;

            // Chiavi per accedere al tiratore corrente
            const actualKey = selectedTeamSide === 'A' ? 'actualFencerA' : 'actualFencerB';
            const originalKey = selectedTeamSide === 'A' ? 'fencerA' : 'fencerB';

            // Ottieni il tiratore attuale (potrebbe essere già una riserva)
            const currentObj = assalto[actualKey] || assalto[originalKey];
            const currentName = typeof currentObj === 'string' ? currentObj : currentObj?.name;

            if (currentName === selectedFencerToReplace.name) {
                return {
                    ...assalto,
                    [actualKey]: reserve
                };
            }
            return assalto;
        });

        onUpdateAssalti(newAssalti);

        if (onAddSostituzione) {
            onAddSostituzione({
                teamSide: selectedTeamSide,
                originale: selectedFencerToReplace,
                sostituto: reserve,
                dalAssalto: currentAssalto,
                timestamp: Date.now(),
                tipo: 'riserva'
            });
        }
        setReserveName('');
        setSelectedFencerToReplace(null);
        setSelectedTeamSide(null);
    };

    // Helper per ottenere tiratori unici (per selezione riserva)
    const getUniqueFencers = (teamSide) => {
        const seen = new Set();
        const list = [];
        assalti.forEach(a => {
            const f = getActualFencer(a, teamSide);
            if (f && f.name && !seen.has(f.name)) {
                seen.add(f.name);
                list.push(f);
            }
        });
        return list;
    };

    const renderFencerSelect = (idx, teamSide, value, options) => (
        <View style={styles.selectContainer}>
            <View style={styles.selectButtons}>
                {options.map((m, optIdx) => (
                    <TouchableOpacity
                        key={optIdx}
                        style={[styles.selectOption, value === optIdx && styles.selectOptionActive]}
                        onPress={() => {
                            if (matchStarted) return;
                            const newSel = [...finalSelection];
                            newSel[idx][teamSide === 'A' ? 0 : 1] = optIdx;
                            setFinalSelection(newSel);
                        }}
                        disabled={matchStarted}
                    >
                        <Text style={[styles.selectOptionText, value === optIdx && styles.selectOptionTextActive]}>
                            {m.name}
                        </Text>
                    </TouchableOpacity>
                ))}
            </View>
        </View>
    );

    const renderSequenzaTab = () => (
        <ScrollView style={styles.tabContent}>
            {matchStarted && (
                <View style={styles.warningBox}>
                    <WarningIcon style={{ fontSize: 20, color: '#e65100', marginRight: 8 }} />
                    <Text style={styles.warningText}>Match già iniziato - non modificabile</Text>
                </View>
            )}
            {!matchStarted && (
                <View style={styles.helpBox}>
                    <Text style={styles.helpText}>
                        Imposta i tiriatori per gli ultimi {numFinalBouts} assalti.
                        La sequenza precedente sarà calcolata automaticamente (FIE).
                    </Text>
                </View>
            )}

            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                <FencingSwordsIcon style={{ fontSize: 20, color: '#c2185b', marginRight: 6 }} />
                <Text style={styles.sectionTitle}>Ultimi {numFinalBouts} Assalti</Text>
            </View>

            {Array.from({ length: numFinalBouts }).map((_, i) => {
                const assaltoNum = startOfFinal + i + 1;
                const sel = finalSelection[i] || [null, null];
                return (
                    <View key={i} style={styles.lastRow}>
                        <Text style={styles.assaltoLabel}>Assalto {assaltoNum}</Text>
                        <View style={styles.rowSelectors}>
                            <View style={{ flex: 1 }}>{renderFencerSelect(i, 'A', sel[0], teamAMembers)}</View>
                            <Text style={styles.vsText}>VS</Text>
                            <View style={{ flex: 1 }}>{renderFencerSelect(i, 'B', sel[1], teamBMembers)}</View>
                        </View>
                    </View>
                );
            })}

            {!matchStarted && (
                <TouchableOpacity style={styles.confirmBtnFull} onPress={generateFullSequence}>
                    <CheckIcon style={{ fontSize: 20, color: '#fff', marginRight: 8 }} />
                    <Text style={styles.confirmBtnText}>Conferma Sequenza</Text>
                </TouchableOpacity>
            )}

            {/* Preview elenco completo */}
            <View style={styles.previewSection}>
                <Text style={styles.previewTitle}>Sequenza Completa:</Text>
                {assalti.map((a, idx) => {
                    const nA = typeof a.fencerA === 'string' ? a.fencerA : a.fencerA?.name;
                    const nB = typeof a.fencerB === 'string' ? a.fencerB : a.fencerB?.name;
                    const isLast = idx >= startOfFinal;
                    return (
                        <Text key={idx} style={[styles.previewItem, isLast && styles.previewItemStrategic]}>
                            {idx + 1}. {nA} vs {nB} {a.completed ? <CheckCircleIcon style={{ fontSize: 14, color: '#4caf50', marginLeft: 4 }} /> : ''}
                        </Text>
                    );
                })}
            </View>
        </ScrollView>
    );

    const renderRiservaTab = () => (
        <ScrollView style={styles.tabContent}>
            <Text style={styles.helpText}>Seleziona chi esce e scrivi il nome di chi entra.</Text>

            <Text style={styles.sectionTitle}>🔴 {teamA?.name}</Text>
            <View style={styles.fencerList}>
                {getUniqueFencers('A').map((f, i) => (
                    <TouchableOpacity
                        key={i}
                        style={[styles.fencerChip, selectedFencerToReplace?.name === f.name && selectedTeamSide === 'A' && styles.chipSelected]}
                        onPress={() => { setSelectedFencerToReplace(f); setSelectedTeamSide('A'); }}
                    >
                        <Text>{f.name}</Text>
                    </TouchableOpacity>
                ))}
            </View>

            <Text style={[styles.sectionTitle, { marginTop: 16 }]}>🟢 {teamB?.name}</Text>
            <View style={styles.fencerList}>
                {getUniqueFencers('B').map((f, i) => (
                    <TouchableOpacity
                        key={i}
                        style={[styles.fencerChip, selectedFencerToReplace?.name === f.name && selectedTeamSide === 'B' && styles.chipSelected]}
                        onPress={() => { setSelectedFencerToReplace(f); setSelectedTeamSide('B'); }}
                    >
                        <Text>{f.name}</Text>
                    </TouchableOpacity>
                ))}
            </View>

            {selectedFencerToReplace && (
                <View style={styles.reserveForm}>
                    <Text style={{ fontWeight: 'bold', marginBottom: 8 }}>
                        Riserva per {selectedFencerToReplace.name}:
                    </Text>
                    <TextInput
                        style={styles.input}
                        placeholder="Nome riserva..."
                        value={reserveName}
                        onChangeText={setReserveName}
                    />
                    <TouchableOpacity
                        style={[styles.confirmBtnFull, !reserveName.trim() && { backgroundColor: '#ccc' }]}
                        disabled={!reserveName.trim()}
                        onPress={handleAddReserve}
                    >
                        <Text style={styles.confirmBtnText}>Sostituisci</Text>
                    </TouchableOpacity>
                </View>
            )}
        </ScrollView>
    );

    return (
        <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
            <View style={styles.overlay}>
                <View style={styles.modal}>
                    <View style={styles.header}>
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <FencingMaskIcon style={{ fontSize: 22, color: '#fff', marginRight: 10 }} />
                            <Text style={styles.title}>Gestione Match ({teamSize}v{teamSize})</Text>
                        </View>
                        <TouchableOpacity onPress={onClose}><Text style={styles.closeText}>✕</Text></TouchableOpacity>
                    </View>
                    <View style={styles.tabs}>
                        <TouchableOpacity style={[styles.tab, activeTab === 'sequenza' && styles.tabActive]} onPress={() => setActiveTab('sequenza')}>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <AssignmentIcon style={{ fontSize: 18, color: activeTab === 'sequenza' ? '#1976d2' : '#666', marginRight: 6 }} />
                                <Text style={{ color: activeTab === 'sequenza' ? '#1976d2' : '#666' }}>Sequenza</Text>
                            </View>
                        </TouchableOpacity>
                        <TouchableOpacity style={[styles.tab, activeTab === 'riserva' && styles.tabActive]} onPress={() => setActiveTab('riserva')}>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <AddIcon style={{ fontSize: 18, color: activeTab === 'riserva' ? '#1976d2' : '#666', marginRight: 6 }} />
                                <Text style={{ color: activeTab === 'riserva' ? '#1976d2' : '#666' }}>Riserva</Text>
                            </View>
                        </TouchableOpacity>
                    </View>
                    {activeTab === 'sequenza' ? renderSequenzaTab() : renderRiservaTab()}
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' },
    modal: { width: '95%', maxWidth: 500, maxHeight: '90%', backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden' },
    header: { padding: 16, backgroundColor: '#1976d2', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    title: { color: '#fff', fontSize: 18, fontWeight: 'bold' },
    closeText: { color: '#fff', fontSize: 20 },
    tabs: { flexDirection: 'row', borderBottomWidth: 1, borderColor: '#eee' },
    tab: { flex: 1, padding: 12, alignItems: 'center' },
    tabActive: { borderBottomWidth: 3, borderColor: '#1976d2' },
    tabContent: { padding: 12 },
    helpBox: { backgroundColor: '#e3f2fd', padding: 10, borderRadius: 8, marginBottom: 10 },
    helpText: { color: '#1565c0', fontSize: 13, textAlign: 'center' },
    warningBox: { backgroundColor: '#fff3e0', padding: 10, borderRadius: 8, marginBottom: 10 },
    warningText: { color: '#e65100', textAlign: 'center' },
    sectionTitle: { fontWeight: 'bold', fontSize: 15, marginBottom: 8 },
    lastRow: { backgroundColor: '#fce4ec', padding: 10, borderRadius: 8, marginBottom: 10, borderLeftWidth: 4, borderColor: '#e91e63' },
    assaltoLabel: { color: '#c2185b', fontWeight: 'bold', marginBottom: 6 },
    rowSelectors: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    vsText: { marginHorizontal: 8, color: '#888', fontWeight: 'bold' },
    selectContainer: { flexDirection: 'column' },
    selectButtons: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
    selectOption: { padding: 6, borderRadius: 4, borderWidth: 1, borderColor: '#ddd', backgroundColor: '#fff', minWidth: 60, alignItems: 'center' },
    selectOptionActive: { backgroundColor: '#1976d2', borderColor: '#1976d2' },
    selectOptionText: { fontSize: 12 },
    selectOptionTextActive: { color: '#fff', fontWeight: 'bold' },
    confirmBtnFull: { backgroundColor: '#4caf50', padding: 12, borderRadius: 8, alignItems: 'center', marginTop: 10 },
    confirmBtnText: { color: '#fff', fontWeight: 'bold' },
    previewSection: { marginTop: 20, backgroundColor: '#f9f9f9', padding: 10, borderRadius: 8 },
    previewTitle: { fontWeight: 'bold', marginBottom: 5 },
    previewItem: { fontSize: 12, paddingVertical: 2 },
    previewItemStrategic: { color: '#c2185b', fontWeight: 'bold' },
    fencerList: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    fencerChip: { padding: 8, backgroundColor: '#f0f0f0', borderRadius: 20, borderWidth: 1, borderColor: 'transparent' },
    chipSelected: { backgroundColor: '#e3f2fd', borderColor: '#1976d2' },
    reserveForm: { marginTop: 20, padding: 15, backgroundColor: '#f5f5f5', borderRadius: 8 },
    input: { backgroundColor: '#fff', borderWidth: 1, borderColor: '#ddd', borderRadius: 8, padding: 10, marginBottom: 10 }
});

export default MatchManagerModal;
